'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

export type CollapsedSide = 'left' | 'right' | null

export const DEFAULT_RATIO = 0.5
export const MIN_RATIO = 0.15
export const MAX_RATIO = 0.85
// Keyboard nudge per arrow press.
export const RATIO_STEP = 0.02
// Drag this far past the clamp toward an edge and the panel snaps shut.
export const SNAP_RATIO = 0.07

interface PairState {
  collapsed: CollapsedSide
  ratio: number
}

export const clampRatio = (value: number) => Math.min(MAX_RATIO, Math.max(MIN_RATIO, value))

export const useCollapsiblePair = (localStorageKey: string, defaultCollapsedSide: CollapsedSide = null) => {
  // Start from the default on both server and first client render to avoid a
  // hydration mismatch; adopt the persisted value only after mount.
  const [collapsed, setCollapsed] = useState<CollapsedSide>(defaultCollapsedSide)
  const [ratio, setRatioState] = useState<number>(DEFAULT_RATIO)

  // Assume the resize hint was already seen so nothing renders on the server or
  // first client paint; reveal it only after mount if storage says otherwise.
  const [hintSeen, setHintSeen] = useState(true)
  useEffect(() => {
    if (!readFlag(`${localStorageKey}:hint`)) setHintSeen(false)
  }, [localStorageKey])

  const dismissHint = useCallback(() => {
    setHintSeen(true)
    writeFlag(`${localStorageKey}:hint`, true)
  }, [localStorageKey])

  // Keep latest values for persistence without recreating commit callbacks.
  const stateRef = useRef<PairState>({ collapsed, ratio })
  useEffect(() => {
    stateRef.current = { collapsed, ratio }
  }, [collapsed, ratio])

  useEffect(() => {
    const stored = readPairState(localStorageKey)
    if (stored?.collapsed !== undefined) setCollapsed(stored.collapsed)
    if (stored?.ratio !== undefined) setRatioState(stored.ratio)
  }, [localStorageKey])

  const persist = useCallback((next: PairState) => writePairState(localStorageKey, next), [localStorageKey])

  const commit = useCallback(
    (next: CollapsedSide) => {
      setCollapsed(next)
      persist({ ...stateRef.current, collapsed: next })
    },
    [persist],
  )

  // Update the ratio without touching storage — safe to call on every drag move.
  const setRatio = useCallback((next: number) => setRatioState(clampRatio(next)), [])

  // Persist the ratio once an adjustment settles (drag end, keyboard, reset).
  // Pass the value when it was just set this tick, since stateRef lags a render.
  const commitRatio = useCallback(
    (next?: number) => {
      const ratio = next === undefined ? stateRef.current.ratio : clampRatio(next)
      persist({ ...stateRef.current, ratio })
    },
    [persist],
  )

  const collapse = useCallback((side: 'left' | 'right') => commit(side), [commit])
  // Re-open to an even split so the divider lands back in the middle.
  const expand = useCallback(() => {
    setRatioState(DEFAULT_RATIO)
    setCollapsed(null)
    persist({ collapsed: null, ratio: DEFAULT_RATIO })
  }, [persist])
  const toggle = useCallback((side: 'left' | 'right') => commit(collapsed === side ? null : side), [commit, collapsed])

  return { collapsed, ratio, setRatio, commitRatio, collapse, expand, toggle, hintSeen, dismissHint }
}

const readPairState = (key: string): Partial<PairState> | undefined => {
  if (typeof window === 'undefined') return undefined

  const value = localStorage.getItem(key)
  if (!value) return undefined

  try {
    const parsed = JSON.parse(value)

    // Legacy format: a bare CollapsedSide value.
    if (parsed === null || parsed === 'left' || parsed === 'right') return { collapsed: parsed }

    if (typeof parsed !== 'object') return undefined

    const collapsed =
      parsed.collapsed === null || parsed.collapsed === 'left' || parsed.collapsed === 'right'
        ? parsed.collapsed
        : undefined
    const ratio = typeof parsed.ratio === 'number' ? clampRatio(parsed.ratio) : undefined

    return { collapsed, ratio }
  } catch {
    return undefined
  }
}

const writePairState = (key: string, value: PairState) => {
  localStorage.setItem(key, JSON.stringify(value))
}

const readFlag = (key: string): boolean => {
  if (typeof window === 'undefined') return false

  return localStorage.getItem(key) === 'true'
}

const writeFlag = (key: string, value: boolean) => {
  try {
    localStorage.setItem(key, String(value))
  } catch {
    // Storage unavailable (private mode / quota) — the hint just shows again next time.
  }
}
